import React from 'react';

import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';

import { Input } from '~/components/ui/input';
import { Spinner } from '~/components/ui/spinner';
import { cn } from '~/lib/utils';

import s from './city-search.module.css';

export type SelectedCity = {
  name: string;
  administrative: string | undefined;
  latlng: {
    lat: number;
    lng: number;
  };
};

interface CitySearchProps {
  onSelectCity: (suggestion: SelectedCity) => void;
  onClear: () => void;
}

export function CitySearch({ onSelectCity, onClear }: CitySearchProps) {
  const [value, setValue] = React.useState('');
  const [showPredictions, setShowPredictions] = React.useState(false);

  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } = usePlacesService({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    debounce: 1000,
    options: {
      types: ['(regions)'],
      componentRestrictions: { country: 'us' },
    },
  });

  const handleSelect = (place: any) => {
    setValue(place.description);
    setShowPredictions(false);

    placesService?.getDetails(
      {
        placeId: place.place_id,
      },
      (placeDetails: any) => {
        const administrative = placeDetails.address_components.filter((c: any) =>
          c.types.includes('administrative_area_level_1'),
        )?.[0];

        onSelectCity({
          name: placeDetails.vicinity,
          administrative: administrative ? administrative.long_name : '',
          latlng: {
            lat: placeDetails.geometry.location.lat(),
            lng: placeDetails.geometry.location.lng(),
          },
        });
      },
    );
  };

  React.useEffect(() => {
    if (value === '') {
      onClear();
    }
  }, [onClear, value]);

  return (
    <div className={cn('mx-auto max-w-md', s.root)}>
      <Input
        id='city-search'
        onChange={(evt) => {
          getPlacePredictions({ input: evt.target.value });
          setValue(evt.target.value);
          setShowPredictions(true);
        }}
        placeholder='Type a city or zip code'
        value={value.replace(', USA', '')}
      />
      {showPredictions && (
        <div className='border-border/50 mt-2 flex flex-col overflow-hidden rounded-lg border p-1 shadow-lg'>
          {isPlacePredictionsLoading && <Spinner className='text-blue mx-auto my-3' />}
          {placePredictions?.map((place) => (
            <button
              className='font-greycliff-bold point hover:bg-secondary/5 w-full justify-start rounded px-5 py-3'
              key={place.place_id}
              onClick={() => handleSelect(place)}
            >
              {place.description.replace(', USA', '')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
